import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Card, Button, Alert, Spinner, Modal, Form } from 'react-bootstrap'
import { FaPen, FaPlus, FaTrash } from 'react-icons/fa'
import { useAuth } from '../../../../auth/auth-context'
import useFetch from '../../../../_fetch'
import ScriptCard from './script-card'
import { IPublisher } from '../types'
import ScriptSettingsEditor from './script-settings-editor'
import {
    ScriptOptionSets,
    ScriptSettingsState,
    createEmptyScriptSettings,
    createDefaultScriptSettings,
    normalizeScriptSettingsState,
    serializeScriptSettingsState,
} from './script-settings-model'

const MAX_SCRIPT_COUNT = 5

function buildScriptsPayload(scripts: ScriptSettingsState[], scriptOptions: ScriptOptionSets) {
    const [defaultScript, ...additionalScripts] = scripts

    return {
        ...serializeScriptSettingsState(defaultScript, scriptOptions),
        additionalScripts: additionalScripts.map((script) =>
            serializeScriptSettingsState(script, scriptOptions)
        ),
    }
}

const LinkScriptSettings: React.FC<{publishers: IPublisher[]}> = ({ publishers }) => {
    const { user } = useAuth()
    const { doFetch } = useFetch()
    const userId = (user as any)?.id
    const emailUsernameRegex = /^[^\s@]+@[^\s@]+\.com$/i
    const usernameForScript = emailUsernameRegex.test(user?.username ?? '')
        ? (userId != null ? String(userId) : undefined)
        : user?.username

    const [saved, setSaved] = useState(false)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [showRenameModal, setShowRenameModal] = useState(false)
    const [renameDraft, setRenameDraft] = useState('')
    const [error, setError] = useState<string | null>(null)

    const [scriptOptions, setScriptOptions] = useState<ScriptOptionSets>({
        campaigns: [],
        networks: [],
        countries: [],
        channels: [],
    })
    const [scripts, setScripts] = useState<ScriptSettingsState[]>([createEmptyScriptSettings()])
    const [lastSavedSignature, setLastSavedSignature] = useState('')
    const [savedScriptCount, setSavedScriptCount] = useState(1)
    const [activeScriptIndex, setActiveScriptIndex] = useState(0)

    const loadSettings = useCallback(async () => {
        try {
            setLoading(true)
            setError(null)

            const [res, resInfo] = await Promise.all([
                doFetch('/byelink/settings'),
                doFetch('/data/relation-info-full'),
            ])

            if (!res.ok) throw new Error('Failed to load settings')
            if (!resInfo.ok) throw new Error('Failed to load relation info')

            const data = await res.json()
            const info = await resInfo.json()

            const validCampaignIds: Record<string, boolean> = {}
            for (const publisher of publishers) {
                validCampaignIds[`${publisher.id}_${publisher.network}_${publisher.country}`] = true
            }

            const options: ScriptOptionSets = {
                campaigns: info.campaigns.filter((campaign: any) =>
                    validCampaignIds[`${campaign.id}_${campaign.network}_${campaign.country}`]
                ),
                networks: Array.from(new Set(info.networks.map((network: any) => network.network))),
                countries: Array.from(new Set(info.countries.map((country: any) => country.country))),
                channels: info.channels,
            }

            const nextScripts = [
                normalizeScriptSettingsState(data, options),
                ...((Array.isArray(data.additionalScripts) ? data.additionalScripts : [])
                    .slice(0, MAX_SCRIPT_COUNT - 1)
                    .filter(Boolean)
                    .map((script: any) => normalizeScriptSettingsState(script, options))),
            ]

            setScriptOptions(options)
            const resolvedScripts = nextScripts.length ? nextScripts : [createDefaultScriptSettings(options)]
            setScripts(resolvedScripts)
            setLastSavedSignature(JSON.stringify(buildScriptsPayload(resolvedScripts, options)))
            setSavedScriptCount(resolvedScripts.length)
            setActiveScriptIndex((currentIndex) => Math.min(currentIndex, resolvedScripts.length - 1, MAX_SCRIPT_COUNT - 1))
        } catch (e: any) {
            setError(e.message)
        } finally {
            setLoading(false)
        }
    }, [doFetch, publishers])

    useEffect(() => {
        if (user) loadSettings()
    }, [user, loadSettings])

    const persistScripts = async (nextScripts: ScriptSettingsState[]) => {
        try {
            setError(null)
            setSaved(false)

            const res = await doFetch('/byelink/settings', buildScriptsPayload(nextScripts, scriptOptions))

            if (!res.ok) throw new Error('Failed to save settings')
            return true
        } catch (e: any) {
            setError(e.message)
            return false
        }
    }

    const handleSave = async () => {
        try {
            setSaving(true)
            setError(null)
            setSaved(false)

            const didSave = await persistScripts(scripts)
            if (!didSave) return

            setLastSavedSignature(JSON.stringify(buildScriptsPayload(scripts, scriptOptions)))
            setSavedScriptCount(scripts.length)
            setSaved(true)
            setTimeout(() => setSaved(false), 2000)
        } finally {
            setSaving(false)
        }
    }

    const updateScript = (index: number, nextValue: ScriptSettingsState) => {
        setScripts((currentScripts) =>
            currentScripts.map((script, scriptIndex) =>
                scriptIndex === index ? nextValue : script
            )
        )
    }

    const handleAddScript = () => {
        setScripts((currentScripts) => {
            if (currentScripts.length >= MAX_SCRIPT_COUNT) return currentScripts

            const nextScripts = [...currentScripts, createDefaultScriptSettings(scriptOptions)]
            setActiveScriptIndex(nextScripts.length - 1)
            return nextScripts
        })
    }

    const handleConfirmDelete = async () => {
        const nextScripts = scripts.slice(0, -1)
        if (!nextScripts.length) return

        try {
            setDeleting(true)
            const didSave = await persistScripts(nextScripts)
            if (!didSave) return

            setScripts(nextScripts)
            setLastSavedSignature(JSON.stringify(buildScriptsPayload(nextScripts, scriptOptions)))
            setSavedScriptCount(nextScripts.length)
            setActiveScriptIndex((currentIndex) => Math.min(currentIndex, nextScripts.length - 1))
            setShowDeleteModal(false)
            setSaved(true)
            setTimeout(() => setSaved(false), 2000)
        } finally {
            setDeleting(false)
        }
    }

    const activeScript = scripts[activeScriptIndex] ?? scripts[0]
    const canAddScript = scripts.length < MAX_SCRIPT_COUNT
    const currentScriptNumber = activeScriptIndex + 1
    const activeScriptCanCopy = currentScriptNumber <= savedScriptCount
    const lastScriptIndex = scripts.length - 1
    const currentSignature = useMemo(
        () => JSON.stringify(buildScriptsPayload(scripts, scriptOptions)),
        [scripts, scriptOptions]
    )
    const hasUnsavedChanges = !loading && currentSignature !== lastSavedSignature

    const getDefaultScriptName = (scriptNumber: number) => (
        scriptNumber === 1 ? 'Default script' : `Script ${scriptNumber}`
    )

    const getScriptLabel = (script: ScriptSettingsState, scriptNumber: number) => (
        script.name.trim() || getDefaultScriptName(scriptNumber)
    )

    const openRenameModal = () => {
        setRenameDraft(activeScript.name || getDefaultScriptName(currentScriptNumber))
        setShowRenameModal(true)
    }

    const handleRenameSave = () => {
        const trimmedName = renameDraft.trim()
        const defaultName = getDefaultScriptName(currentScriptNumber)

        updateScript(activeScriptIndex, {
            ...activeScript,
            name: !trimmedName || trimmedName === defaultName ? '' : trimmedName.slice(0, 80),
        })

        setShowRenameModal(false)
    }

    const addScriptButton = (
        <Button variant="outline-primary" onClick={handleAddScript} disabled={!canAddScript} size="sm">
            <FaPlus className="me-2" />
            New script
        </Button>
    )

    const addScriptHelper = (
        <div className="d-flex flex-column align-items-start gap-2">
            {addScriptButton}
            <div className="text-muted small">
                Add a separate script with its own domains, campaign filters, and rules.
            </div>
        </div>
    )

    if (loading) {
        return (
            <div className="d-flex justify-content-center mt-4">
                <Spinner animation="border" />
            </div>
        )
    }

    return (
        <div className="w-100">
            {error && <Alert variant="danger">{error}</Alert>}

            {scripts.length > 1 && (
                <Card className="shadow-sm border-0 mb-4">
                    <Card.Body className="p-3">
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                            <div className="d-flex flex-wrap align-items-center gap-2">
                                {scripts.map((script, index) => {
                                    const scriptNumber = index + 1
                                    const isActive = index === activeScriptIndex

                                    return (
                                        <div key={`script-tab-${scriptNumber}`} className="d-flex align-items-center gap-1">
                                            <Button
                                                variant={isActive ? 'primary' : 'outline-secondary'}
                                                size="sm"
                                                onClick={() => setActiveScriptIndex(index)}
                                            >
                                                {getScriptLabel(script, scriptNumber)}
                                            </Button>
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className={`p-0 ${isActive ? 'text-muted' : 'invisible'}`}
                                                onClick={openRenameModal}
                                                aria-label={`Rename ${getScriptLabel(script, scriptNumber)}`}
                                                tabIndex={isActive ? 0 : -1}
                                            >
                                                <FaPen />
                                            </Button>
                                        </div>
                                    )
                                })}
                            </div>

                            <div className="d-flex flex-wrap align-items-center gap-2">
                                {activeScriptIndex === lastScriptIndex && activeScriptIndex > 0 && (
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => setShowDeleteModal(true)}
                                    >
                                        <FaTrash className="me-2" />
                                        Delete script
                                    </Button>
                                )}
                                {addScriptButton}
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            )}

            <ScriptSettingsEditor
                publishers={publishers}
                options={scriptOptions}
                value={activeScript}
                onChange={(nextValue) => updateScript(activeScriptIndex, nextValue)}
            />

            <ScriptCard
                username={usernameForScript}
                scriptNumber={currentScriptNumber}
                canCopy={activeScriptCanCopy}
            />

            {scripts.length === 1 && (
                <Card className="shadow-sm border-0 mb-4">
                    <Card.Body className="px-4 py-3">
                        {addScriptHelper}
                    </Card.Body>
                </Card>
            )}

            <div
                className="position-fixed d-flex align-items-center gap-3"
                style={{ right: '24px', bottom: '24px', zIndex: 1030 }}
            >
                {saved && <span className="text-success small fw-semibold">Saved</span>}
                <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={!hasUnsavedChanges || saving || deleting}
                    style={{ boxShadow: hasUnsavedChanges ? '0 10px 24px rgba(13, 110, 253, 0.22)' : 'none' }}
                >
                    {saving ? 'Saving...' : 'Save settings'}
                </Button>
            </div>

            <Modal show={showDeleteModal} onHide={() => !deleting && setShowDeleteModal(false)} centered>
                <Modal.Header closeButton={!deleting}>
                    <Modal.Title>Delete script</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {`Delete ${getDefaultScriptName(scripts.length)}? This change is saved automatically when you confirm.`}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleConfirmDelete} disabled={deleting}>
                        {deleting ? 'Deleting...' : 'Delete script'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showRenameModal} onHide={() => setShowRenameModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Rename script</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label className="fw-semibold">Script title</Form.Label>
                        <Form.Control
                            type="text"
                            value={renameDraft}
                            onChange={(event) => setRenameDraft(event.target.value)}
                            maxLength={80}
                            autoFocus
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowRenameModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleRenameSave}>
                        Save title
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}

export default LinkScriptSettings
